/* ==========================================================================
   ZELICAT THEME - product.js
   Product page (sections/main-product.liquid): gallery (scroll-snap slider, thumbnails, dots, arrows,
   keyboard), full-screen zoom lightbox (keyboard, swipe, click to zoom), product video, variant picker,
   quantity stepper and the Add to cart button states.

   Variant changes: the option values are matched against the product JSON printed by the section
   (<script data-product-json>), then the URL (?variant=), the hidden id field, the gallery image and the
   availability of the option values are updated at once. Price, buttons and the sticky bar are then
   replaced with server-rendered HTML through the Section Rendering API: every element marked
   [data-zc-swap="key"] is swapped for the same element of the freshly rendered section, so amounts are
   always formatted by Liquid. If that request fails the page simply reloads on ?variant=ID.

   Adding to the cart is done by assets/cart.js (form[data-zc-add-form]); this file only reflects the
   button state ("Adding...", "Added") and tells the sticky bar through the "zc:product:state" event.
   ========================================================================== */
(() => {
  'use strict';
  const ZC = window.ZC;
  if (!ZC) return;
  const { $, $$, reduceMotion } = ZC;
  const S = ZC.config.strings || {};

  /* ------------------------------------------------------------------ gallery */
  function initGallery(root) {
    const api = { goTo() {}, count: 0, resetVideos() {} };
    const g = $('[data-gallery]', root);
    if (!g) return api;
    const viewport = $('[data-viewport]', g);
    let slides = $$('.gallery__slide', g);
    let thumbs = $$('[data-thumb]', g);
    const track = $('[data-track]', g);
    const thumbBox = $('[data-thumbs]', g);
    const thumbBtns = $$('[data-thumbs-scroll]', g);
    const wide = window.matchMedia('(min-width: 990px)');
    const dots = $$('[data-dots] button', g);
    const arrows = $$('[data-arrow]', g);
    const status = $('[data-gallery-status]', g);
    const statusTemplate = status ? status.dataset.template || '' : '';
    let index = 0;
    let ticking = false;
    api.count = slides.length;
    api.slides = slides;

    function paint(i) {
      index = i;
      thumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === i);
        if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      dots.forEach((d, n) => { if (n === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
      arrows.forEach((a) => {
        const dir = Number(a.dataset.arrow);
        a.disabled = (dir < 0 && i === 0) || (dir > 0 && i === slides.length - 1);
      });
      revealThumb(i);
      if (status && statusTemplate) status.textContent = statusTemplate.replace('{number}', i + 1).replace('{total}', slides.length);
      /* slides that are out of view are inert, so keyboard focus never lands on a hidden image */
      slides.forEach((s, n) => { s.inert = n !== i; });
      api.resetVideos(i);
    }
    /* thumbnail rail (desktop): discreet vertical scroll, up / down buttons only when needed */
    function syncThumbBtns() {
      if (!thumbBox || !thumbBtns.length) return;
      const max = thumbBox.scrollHeight - thumbBox.clientHeight;
      thumbBtns.forEach((b) => {
        const dir = Number(b.dataset.thumbsScroll);
        b.hidden = !wide.matches || max <= 2 || (dir < 0 ? thumbBox.scrollTop <= 2 : thumbBox.scrollTop >= max - 2);
      });
    }
    function revealThumb(n) {
      const t = thumbs[n];
      if (!thumbBox || !t || !wide.matches || thumbBox.scrollHeight <= thumbBox.clientHeight + 2) return;
      const pad = 30;
      const top = t.offsetTop - pad;
      const bottom = t.offsetTop + t.offsetHeight + pad - thumbBox.clientHeight;
      let to = null;
      if (top < thumbBox.scrollTop) to = Math.max(0, top);
      else if (bottom > thumbBox.scrollTop) to = bottom;
      if (to !== null) thumbBox.scrollTo({ top: to, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    if (thumbBox) {
      thumbBox.addEventListener('scroll', syncThumbBtns, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(syncThumbBtns).observe(thumbBox);
      if (wide.addEventListener) wide.addEventListener('change', syncThumbBtns);
      thumbBtns.forEach((b) => b.addEventListener('click', () => {
        const t = thumbs[0];
        const step = t ? (t.offsetHeight + 8) * 3 : 200;
        thumbBox.scrollBy({ top: Number(b.dataset.thumbsScroll) * step, behavior: reduceMotion ? 'auto' : 'smooth' });
      }));
    }

    /* the selected colour's images first (same rules as the Liquid that renders the first order) */
    const handleize = (v) => String(v).toLowerCase().normalize('NFD').replace(new RegExp('[' + String.fromCharCode(768) + '-' + String.fromCharCode(879) + ']', 'g'), '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    function rank(tags, sel) {
      const byOption = {};
      String(tags || '').split(/\s+/).filter(Boolean).forEach((t) => {
        const at = t.indexOf(':');
        if (at < 0) return;
        (byOption[t.slice(0, at)] = byOption[t.slice(0, at)] || new Set()).add(t.slice(at + 1));
      });
      let has0 = false, has2 = false, has3 = false;
      sel.forEach((value, oi) => {
        const set = byOption[oi];
        if (!set || !set.size) return;
        const inSel = set.has(value);
        const others = set.size > (inSel ? 1 : 0);
        if (inSel && others) has2 = true; else if (inSel) has0 = true; else has3 = true;
      });
      return has3 ? 3 : has2 ? 2 : has0 ? 0 : 1;
    }
    /* firstId (optional): media id to put first, e.g. the selected variant's own or pack image
       (standard product page only); the other images of the colour follow in their usual order */
    api.prioritize = function (options, firstId) {
      if (g.dataset.variantPriority !== 'true' || !track || !slides.length) return false;
      const sel = options.map(handleize);
      const ranked = slides.map((sl) => ({
        sl,
        th: thumbs.find((t) => t.dataset.mediaIndex === sl.dataset.mediaIndex) || null,
        p: firstId && Number(sl.dataset.mediaId) === firstId ? -1 : rank(sl.dataset.tags, sel),
        i: Number(sl.dataset.mediaIndex),
      }));
      /* nothing is actually tagged for the selected options (no colour option, or the images are not
         linked to it): reordering would be a no-op, so leave the slide choice to the variant's own
         media (v.mediaId in paint()) instead of forcing the gallery back to the first slide */
      if (!ranked.some((x) => x.p === 0 || x.p === 2)) return false;
      const list = ranked.sort((a, b) => a.p - b.p || a.i - b.i);
      list.forEach((x) => { track.appendChild(x.sl); if (x.th && thumbBox) thumbBox.appendChild(x.th); });
      slides = list.map((x) => x.sl);
      thumbs = list.map((x) => x.th).filter(Boolean);
      api.slides = slides;
      const fill = (tpl, n) => String(tpl || '').replace('{number}', n).replace('{total}', slides.length);
      slides.forEach((sl, n) => {
        sl.setAttribute('aria-label', fill(g.dataset.labelSlide, n + 1));
        const open = $('[data-lightbox]', sl);
        if (open) open.setAttribute('aria-label', fill(g.dataset.labelOpen, n + 1));
      });
      thumbs.forEach((t, n) => { t.dataset.thumb = n; t.setAttribute('aria-label', fill(g.dataset.labelThumb, n + 1)); });
      if (thumbBox) thumbBox.scrollTop = 0;
      goTo(0, false);
      syncThumbBtns();
      return true;
    };
    /* true only when at least one slide actually carries a colour/option tag: when the setting is on
       but no media is tagged (e.g. options like pack size, not colour), there is nothing for
       prioritize() to do, so deep links should fall back to the variant's own media (mediaId) below */
    api.priority = g.dataset.variantPriority === 'true' && slides.some((sl) => (sl.dataset.tags || '').trim() !== '');

    // Distance between two slide starts (slide width + optional gap); keeps snapping exact on fractional widths.
    const step = () => (slides.length > 1 ? slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left : viewport.clientWidth) || viewport.clientWidth;

    function goTo(i, smooth = true) {
      if (!viewport || !slides.length) return;
      i = Math.max(0, Math.min(slides.length - 1, i));
      viewport.scrollTo({ left: i * step(), behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      paint(i);
    }
    api.goTo = goTo;
    api.current = () => index;

    if (viewport) {
      viewport.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          if (!viewport.clientWidth) return;
          const i = Math.round(viewport.scrollLeft / step());
          if (i !== index && slides[i]) paint(i);
        });
      }, { passive: true });
      viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
      });
      if ('ResizeObserver' in window) new ResizeObserver(() => goTo(index, false)).observe(viewport);
    }
    thumbs.forEach((t) => t.addEventListener('click', () => goTo(Number(t.dataset.thumb))));
    dots.forEach((d, n) => d.addEventListener('click', () => goTo(n)));
    arrows.forEach((a) => a.addEventListener('click', () => goTo(index + Number(a.dataset.arrow))));
    paint(0);
    syncThumbBtns();
    return api;
  }

  /* ------------------------------------------------------------------ lightbox (full screen zoom) */
  function initLightbox(root, gallery) {
    const dlg = $('[data-lightbox-dialog]', root);
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const img = $('[data-lb-img]', dlg);
    const figure = $('[data-lb-figure]', dlg);
    const items = () => $$('[data-lightbox]', root);
    if (!items().length) return;
    let current = 0;

    function show(i) {
      const list = items();
      current = (i + list.length) % list.length;
      img.src = list[current].dataset.full;
      img.alt = ($('img', list[current]) || {}).alt || '';
      figure.classList.remove('is-zoomed');
      img.style.transformOrigin = '';
    }
    root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lightbox]');
      if (!trigger) return;
      show(items().indexOf(trigger));
      dlg.showModal();
    });
    $('[data-lb-close]', dlg).addEventListener('click', () => dlg.close());
    $$('[data-lb-step]', dlg).forEach((b) => b.addEventListener('click', () => show(current + Number(b.dataset.lbStep))));
    dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target === figure) dlg.close(); });
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') show(current + 1);
      if (e.key === 'ArrowLeft') show(current - 1);
    });

    /* click = zoom in at the pointer, pointer move = pan */
    const setOrigin = (e) => {
      const r = img.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
      img.style.transformOrigin = `${x}% ${y}%`;
    };
    let swiped = false;
    img.addEventListener('click', (e) => {
      if (swiped) return;
      if (figure.classList.toggle('is-zoomed')) setOrigin(e);
    });
    img.addEventListener('pointermove', (e) => { if (figure.classList.contains('is-zoomed') && e.pointerType === 'mouse') setOrigin(e); });

    /* swipe left / right to change image (not while zoomed) */
    let start = null;
    figure.addEventListener('pointerdown', (e) => { start = { x: e.clientX, y: e.clientY }; });
    figure.addEventListener('pointerup', (e) => {
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      start = null;
      if (figure.classList.contains('is-zoomed') || Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      swiped = true;
      setTimeout(() => { swiped = false; }, 0);
      show(current + (dx < 0 ? 1 : -1));
    });
    figure.addEventListener('pointercancel', () => { start = null; });

    /* closing the viewer brings the gallery to the image that was being viewed */
    dlg.addEventListener('close', () => {
      const slide = items()[current] && items()[current].closest('.gallery__slide');
      const n = slide && gallery.slides ? gallery.slides.indexOf(slide) : -1;
      if (n > -1) gallery.goTo(n, false);
    });
  }

  /* ------------------------------------------------------------------ product video (poster -> player on click) */
  function initVideos(root, gallery) {
    const boxes = $$('[data-video]', root);
    if (!boxes.length) return;
    const stop = (box) => {
      const player = box.querySelector('.gallery__player');
      if (!player) return;
      player.remove();
      Array.from(box.children).forEach((c) => { c.hidden = false; });
      box.classList.remove('is-playing');
    };
    boxes.forEach((box) => {
      const play = $('[data-play]', box);
      const tpl = $('template[data-video-template]', box);
      if (!play || !tpl) return;
      play.addEventListener('click', () => {
        const node = tpl.content.firstElementChild && tpl.content.firstElementChild.cloneNode(true);
        if (!node) return;
        node.classList.add('gallery__player');
        Array.from(box.children).forEach((c) => { if (c !== tpl) c.hidden = true; });
        box.appendChild(node);
        box.classList.add('is-playing');
        if (node.tagName === 'VIDEO') {
          node.setAttribute('playsinline', '');
          const p = node.play();
          if (p && p.catch) p.catch(() => { /* the visitor can press play in the controls */ });
        } else if (node.tagName === 'IFRAME') {
          node.src += (node.src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
        }
      });
    });
    /* leaving a slide stops its video */
    gallery.resetVideos = (keep) => {
      boxes.forEach((box) => {
        const slide = box.closest('.gallery__slide');
        if (gallery.slides && gallery.slides.indexOf(slide) !== keep) stop(box);
      });
    };
  }

  /* ------------------------------------------------------------------ variants */
  function initVariants(root, gallery) {
    const jsonEl = $('[data-product-json]', root);
    if (!jsonEl) return null;
    let data;
    try { data = JSON.parse(jsonEl.textContent); } catch (e) { return null; }
    if (!data.variants || !data.variants.length) return null;

    const api = { variant: data.variants.find((v) => v.id === data.current) || data.variants[0] };
    const mediaFirst = root.dataset.variantMediaFirst === 'true';
    const groups = $$('[data-option]', root);
    const idInput = $('[data-variant-id]', root);
    if (idInput) idInput.disabled = false;   /* markup keeps it disabled so the no-JS <select name="id"> wins */

    const readSelection = () => groups.map((g) => {
      const r = $('input:checked', g);
      if (r) return r.value;
      const s = $('select', g);
      return s ? s.value : null;
    });
    const find = (sel) => data.variants.find((v) => v.options.every((val, i) => sel[i] === null || sel[i] === undefined || sel[i] === val));

    /* a value is "unavailable" when no variant combines it with the other current selections;
       "sold out" when such variants exist but none can be bought */
    function markValues(sel) {
      groups.forEach((g, i) => {
        $$('label.pill, label.swatch', g).forEach((lab) => {
          const input = $('input', lab);
          if (!input) return;
          const test = sel.map((s, j) => (j === i ? input.value : s));
          const matches = data.variants.filter((v) => v.options.every((x, j) => x === test[j]));
          lab.classList.toggle('is-unavailable', matches.length === 0);
          lab.classList.toggle('is-soldout', matches.length > 0 && !matches.some((v) => v.available));
        });
      });
    }

    let token = 0;
    async function swap(v) {
      const wraps = new Set();
      $$('[data-zc-swap]').forEach((el) => { const w = el.closest('.shopify-section'); if (w) wraps.add(w); });
      const ids = Array.from(wraps).map((w) => w.id.replace('shopify-section-', ''));
      if (!ids.length) return;
      const mine = ++token;
      const url = `${data.url}?variant=${v.id}`;
      try {
        /* no Accept header here: Shopify's Section Rendering API keys its JSON response by the
           requested section id, but when this request carries "Accept: application/json" the
           storefront instead returns the plain product resource (keyed "product"), so sections[id]
           below is always undefined and the swap silently no-ops on every product page. */
        const res = await fetch(`${url}&sections=${ids.join(',')}`);
        if (!res.ok) throw new Error(String(res.status));
        const sections = await res.json();
        if (mine !== token) return;
        Object.keys(sections).forEach((id) => {
          const wrap = document.getElementById(`shopify-section-${id}`);
          if (!wrap || !sections[id]) return;
          const doc = new DOMParser().parseFromString(sections[id], 'text/html');
          const fresh = $$('[data-zc-swap]', doc);
          $$('[data-zc-swap]', wrap).forEach((el) => {
            const next = fresh.find((f) => f.dataset.zcSwap === el.dataset.zcSwap);
            if (next) el.innerHTML = next.innerHTML;
          });
        });
        if (window.Shopify && window.Shopify.PaymentButton && window.Shopify.PaymentButton.init) window.Shopify.PaymentButton.init();
      } catch (err) {
        if (mine === token) window.location.href = url;
      }
    }

    function paint(v, opts) {
      api.variant = v;
      groups.forEach((g, i) => {
        const lab = $('[data-selected]', g);
        if (lab) lab.textContent = v.options[i];
      });
      markValues(v.options);
      if (idInput) idInput.value = v.id;
      root.dispatchEvent(new CustomEvent('zc:variant', { bubbles: true, detail: { variant: v } }));
      if (opts && opts.silent) return;
      if (data.variants.length > 1) {
        try {
          const u = new URL(window.location.href);
          u.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', u.toString());
        } catch (err) { /* history unavailable */ }
      }
      /* standard product page: the variant's "pack image" (variant metafield custom.pack_image), else its own image */
      const lead = mediaFirst ? (v.packMediaId || v.mediaId || null) : null;
      if (gallery.prioritize && gallery.prioritize(v.options, lead)) {
        /* colour images first (led by the variant's image when set): the gallery restarts on slide 1 */
      } else if ((lead || v.mediaId) && gallery.slides) {
        const at = gallery.slides.findIndex((s) => Number(s.dataset.mediaId) === (lead || v.mediaId));
        if (at > -1) gallery.goTo(at);
      }
      swap(v);
    }

    if (groups.length) {
      root.addEventListener('change', (e) => {
        const group = e.target.closest('[data-option]');
        if (!group) return;
        const changed = groups.indexOf(group);
        const sel = readSelection();
        let v = find(sel);
        if (!v) {
          /* the combination does not exist: keep the value just chosen and move the other options to the closest existing variant */
          const cands = data.variants.filter((x) => x.options[changed] === sel[changed]);
          const score = (x) => x.options.reduce((n, val, j) => n + (val === sel[j] ? 1 : 0), 0);
          v = cands.sort((a, b) => score(b) - score(a))[0];
          if (!v) return;
          v.options.forEach((val, j) => {
            $$('input[type="radio"]', groups[j]).forEach((r) => { r.checked = r.value === val; });
            const s = $('select', groups[j]);
            if (s) s.value = val;
          });
          const msg = (root.dataset.strClosest || '').replace('{options}', v.options.filter((x, j) => j !== changed).join(' ' + String.fromCharCode(183) + ' '));
          if (msg) ZC.toast(msg);
        }
        paint(v);
      });
      markValues(api.variant.options);
      /* opened on a ?variant= link: show that variant's image straight away */
      const leadNow = mediaFirst ? (api.variant.packMediaId || api.variant.mediaId || null) : null;
      if (/[?&]variant=/.test(window.location.search) && leadNow && gallery.slides) {
        /* standard product page opened on a ?variant= link: that variant's (pack) image first */
        if (!(gallery.priority && gallery.prioritize(api.variant.options, leadNow))) {
          const at = gallery.slides.findIndex((s) => Number(s.dataset.mediaId) === leadNow);
          if (at > 0) gallery.goTo(at, false);
        }
      } else if (/[?&]variant=/.test(window.location.search) && api.variant.mediaId && gallery.slides && !gallery.priority) {
        const at = gallery.slides.findIndex((s) => Number(s.dataset.mediaId) === api.variant.mediaId);
        if (at > 0) gallery.goTo(at, false);
      }
    }
    return api;
  }

  /* ------------------------------------------------------------------ quantity */
  function initQty(root, variants) {
    $$('[data-qty-group]', root).forEach((group) => {
      const input = $('[data-qty]', group);
      if (!input) return;
      const min = Number(input.min) || 1;
      const baseMax = Number(input.max) || 10;
      const stepBtns = $$('[data-qty-step]', group);
      const maxNow = () => {
        const v = variants && variants.variant;
        return v && v.max !== null && v.max !== undefined ? Math.max(min, Math.min(baseMax, v.max)) : baseMax;
      };
      const clamp = () => {
        const max = maxNow();
        let n = parseInt(input.value, 10);
        if (Number.isNaN(n)) n = min;
        n = Math.max(min, Math.min(max, n));
        input.value = n;
        input.max = max;
        stepBtns.forEach((b) => { b.disabled = (Number(b.dataset.qtyStep) < 0 && n <= min) || (Number(b.dataset.qtyStep) > 0 && n >= max); });
      };
      stepBtns.forEach((b) => b.addEventListener('click', () => {
        input.value = (parseInt(input.value, 10) || min) + Number(b.dataset.qtyStep);
        clamp();
      }));
      input.addEventListener('change', clamp);
      input.addEventListener('input', () => { if (input.value !== '') clamp(); });
      root.addEventListener('zc:variant', clamp);
      clamp();
    });
  }

  /* ------------------------------------------------------------------ add to cart button state */
  function initForm(root) {
    const form = $('form[data-zc-add-form]', root);
    if (!form) return;
    /* dynamic checkout ("Buy it now") buttons submit natively - cart.js must not intercept them */
    form.addEventListener('submit', (e) => {
      const sub = e.submitter;
      if (sub && (sub.name === 'checkout' || sub.closest('.shopify-payment-button'))) e.stopPropagation();
    });

    const wrap = $('[data-zc-swap="atc"]', root);
    if (!wrap || !('MutationObserver' in window)) return;
    let last = 'idle';
    const update = () => {
      const btn = $('[data-zc-atc]', wrap);
      if (!btn) return;
      const label = $('.btn__label', btn) || btn;
      if (!btn.dataset.label) btn.dataset.label = label.textContent;
      let state = 'idle';
      let text = btn.dataset.label;
      if (btn.getAttribute('aria-busy') === 'true') { state = 'adding'; text = S.adding || text; }
      else if (btn.classList.contains('is-added')) { state = 'added'; text = root.dataset.strAdded || text; }
      if (label.textContent !== text) label.textContent = text;
      if (state !== last) {
        last = state;
        document.dispatchEvent(new CustomEvent('zc:product:state', { detail: { state } }));
      }
    };
    new MutationObserver(update).observe(wrap, { attributes: true, attributeFilter: ['aria-busy', 'class'], childList: true, subtree: true });
  }

  /* A closing "Add to cart" button (sections/closing-cta.liquid) submits the main product form:
     the chosen options and quantity are used. Delegated, so it survives section reloads in the editor. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-closing-add]');
    if (!btn || btn.disabled) return;
    const form = $('form[data-zc-add-form][data-product-form]');
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });

  ZC.mount((scope) => {
    $$('[data-product-section]', scope).forEach((root) => {
      if (!ZC.once(root, 'product')) return;
      const gallery = initGallery(root);
      initLightbox(root, gallery);
      initVideos(root, gallery);
      const variants = initVariants(root, gallery);
      initQty(root, variants);
      initForm(root);
    });
  });
})();
